import { useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import axios from "axios";
import { Quote, ChevronLeft, ChevronRight, Star, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emptyForm = { name: "", location: "", quote: "", rating: 5, email: "" };

function Stars({ value = 5, interactive = false, onChange }) {
  return (
    <div className="inline-flex items-center gap-0.5" data-testid="stars-display">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type={interactive ? "button" : undefined}
          onClick={interactive ? () => onChange?.(i) : undefined}
          aria-label={`${i} star`}
          className={`${interactive ? "cursor-pointer" : "cursor-default"} p-0`}
          disabled={!interactive}
          data-testid={interactive ? `star-${i}` : undefined}
        >
          <Star
            size={interactive ? 22 : 14}
            className={i <= value ? "fill-[#FF6B00] text-[#FF6B00]" : "text-slate-300"}
          />
        </button>
      ))}
    </div>
  );
}

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "start", slidesToScroll: 1 });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/testimonials`);
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => {
      setCanPrev(embla.canScrollPrev());
      setCanNext(embla.canScrollNext());
    };
    embla.on("select", onSelect);
    embla.on("reInit", onSelect);
    onSelect();
  }, [embla, items.length]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.quote.trim().length < 10) {
      toast.error("Please share your name and a review of at least 10 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/testimonials`, {
        name: form.name.trim(),
        location: form.location.trim() || null,
        quote: form.quote.trim(),
        rating: Number(form.rating),
        email: form.email.trim() || null,
      });
      toast.success("Thanks! Your review will appear after a quick moderation check.");
      setForm(emptyForm);
      setOpen(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-20 sm:py-28 bg-slate-50" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-widest text-[#FF6B00]">Testimonials</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0B192C] mt-2">What our users say.</h2>
            <p className="mt-3 text-slate-600">Real stories from real Nigerian-Canadian senders.</p>
          </div>
          <div className="flex items-center gap-2">
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-full h-11 px-5 border-2 border-slate-200 hover:border-[#FF6B00] text-[#0B192C] font-semibold"
                  data-testid="leave-review-button"
                >
                  <MessageSquarePlus size={16} className="mr-2" /> Leave a review
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="review-dialog" className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Share your experience</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 mt-2" data-testid="review-form">
                  <div>
                    <Label>Your name *</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Tosin A." data-testid="review-name-input" className="mt-1.5 h-10 rounded-lg" />
                  </div>
                  <div>
                    <Label>City (optional)</Label>
                    <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Calgary, AB" data-testid="review-location-input" className="mt-1.5 h-10 rounded-lg" />
                  </div>
                  <div>
                    <Label>Your rating</Label>
                    <div className="mt-1.5"><Stars value={form.rating} interactive onChange={(r) => setForm({ ...form, rating: r })} /></div>
                  </div>
                  <div>
                    <Label>Your review *</Label>
                    <Textarea rows={5} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} placeholder="What did you love about CashPark?" data-testid="review-quote-input" className="mt-1.5 rounded-lg" />
                  </div>
                  <div>
                    <Label>Email (private — for follow-up only)</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="review-email-input" className="mt-1.5 h-10 rounded-lg" />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                    <Button type="submit" disabled={submitting} data-testid="review-submit-button" className="rounded-full bg-[#FF6B00] hover:bg-[#E65C00] font-semibold">
                      {submitting ? "Submitting…" : "Submit review"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            <button onClick={() => embla?.scrollPrev()} disabled={!canPrev} data-testid="testimonial-prev"
              className="w-11 h-11 rounded-full border border-slate-200 bg-white hover:border-[#0B192C] disabled:opacity-40 flex items-center justify-center transition-colors" aria-label="Previous">
              <ChevronLeft size={18} />
            </button>
            <button onClick={() => embla?.scrollNext()} disabled={!canNext} data-testid="testimonial-next"
              className="w-11 h-11 rounded-full border border-slate-200 bg-white hover:border-[#0B192C] disabled:opacity-40 flex items-center justify-center transition-colors" aria-label="Next">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-slate-500 text-sm" data-testid="testimonials-loading">Loading reviews…</div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-3xl border border-slate-200 p-8 text-center" data-testid="testimonials-empty">
            <p className="text-slate-600">Be the first to leave a review!</p>
            <Button onClick={() => setOpen(true)} className="mt-4 rounded-full bg-[#FF6B00] hover:bg-[#E65C00]">Share your experience</Button>
          </div>
        ) : (
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-5">
              {items.map((t, i) => (
                <div
                  key={t.id || i}
                  className="flex-[0_0_85%] sm:flex-[0_0_50%] lg:flex-[0_0_33.33%] min-w-0"
                  data-testid={`testimonial-card-${i}`}
                >
                  <div className="h-full bg-white rounded-3xl border border-slate-200 p-8 hover:border-[#FF6B00]/60 transition-colors">
                    <div className="flex items-center justify-between">
                      <Quote size={28} className="text-[#FF6B00]" />
                      <Stars value={t.rating || 5} />
                    </div>
                    <p className="mt-5 text-slate-700 leading-relaxed">"{t.quote}"</p>
                    <div className="mt-6 flex items-center gap-3 pt-6 border-t border-slate-100">
                      {t.avatar_url ? (
                        <img src={t.avatar_url} alt={t.name} className="w-11 h-11 rounded-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-11 h-11 rounded-full bg-orange-50 text-[#FF6B00] font-display font-bold flex items-center justify-center">
                          {(t.name || "?").slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="font-display font-semibold text-[#0B192C]">{t.name}</div>
                        {t.location && <div className="text-xs text-slate-500">{t.location}</div>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
